<template>
  <Dialog
    :model-value="modelValue"
    :title="computedTitle"
    :subtitle="subtitle"
    :size="size"
    :body-fit="bodyFit"
    :flush="flush"
    :guard="isGuarded"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="$emit('close')"
    @close-blocked="askAbandon"
  >
    <template v-if="$slots.header" #header>
      <slot name="header"></slot>
    </template>

    <!-- Barre qui ne défile pas avec le corps : une fenêtre à onglets y pose sa
         navigation interne. Cf. le slot `toolbar` de `Dialog`. -->
    <template v-if="$slots.toolbar" #toolbar>
      <slot name="toolbar"></slot>
    </template>

    <!-- L'échec du serveur reste au-dessus de la mise en page du formulaire, y
         compris quand celle-ci prend toute la place (`flush`) : il ne défile pas
         avec elle, et retrouve alors le rembourrage que le corps a perdu. -->
    <div v-if="error && flush" class="dlgf-error-flush">
      <DialogNotice tone="danger" :title="errorTitle">{{ error }}</DialogNotice>
    </div>
    <DialogNotice v-else-if="error" tone="danger" :title="errorTitle">{{ error }}</DialogNotice>

    <form :class="['dlgf-form', { 'dlgf-form-fit': bodyFit }]" @submit.prevent="$emit('save')">
      <slot></slot>
      <!-- Un `<button>` hors écran : sans lui, la touche Entrée ne soumet pas un
           formulaire qui ne contient qu'un seul champ, et le bouton du pied ne
           peut pas être `type="submit"` puisqu'il vit hors du `<form>`. -->
      <button type="submit" class="dlgf-submit" tabindex="-1" aria-hidden="true"></button>
    </form>

    <template v-if="$slots['footer-start']" #footer-start>
      <slot name="footer-start"></slot>
    </template>

    <template #footer>
      <Button type="button" variant="secondary" :disabled="loading" @click="askClose">
        {{ cancelLabel }}
      </Button>
      <Button
        v-if="!hideSave"
        type="button"
        :variant="saveVariant"
        :icon="saveIcon"
        :loading="loading"
        :disabled="saveDisabled"
        @click="$emit('save')"
      >
        {{ loading ? savingLabel : saveLabel }}
      </Button>
    </template>

    <template #overlay>
      <Transition name="dlgf-guard">
        <div v-if="abandonAsked" class="dlgf-guard" role="alertdialog" aria-labelledby="dlgf-guard-title">
          <div class="dlgf-guard-box">
            <h3 id="dlgf-guard-title" class="dlgf-guard-title">Abandonner les modifications ?</h3>
            <p class="dlgf-guard-text">
              Vous avez commencé une saisie sans l'enregistrer. Fermer maintenant la perdra.
            </p>
            <div class="dlgf-guard-actions">
              <Button type="button" variant="secondary" @click="abandonAsked = false">
                Continuer la saisie
              </Button>
              <Button type="button" variant="danger" @click="confirmAbandon">
                Fermer sans enregistrer
              </Button>
            </div>
          </div>
        </div>
      </Transition>
    </template>
  </Dialog>
</template>

<script setup>
/**
 * FENÊTRE DE FORMULAIRE — créer ou modifier.
 *
 * Le remplaçant v2 de `FormModal.vue`. Trois choses de plus, chacune
 * réinventée aujourd'hui par une dizaine de fichiers :
 *
 * 1. `error` — l'échec du serveur s'affiche en tête de corps, au-dessus des
 *    champs, et la saisie reste intacte.
 * 2. `dirty` — quand une saisie est en cours, Échap et la croix ne ferment plus
 *    la fenêtre : ils demandent confirmation. Le clic sur le fond, lui, n'a
 *    jamais rien fermé.
 * 3. Un pied à trois emplacements, dont `footer-start` pour l'action destructive
 *    — à distance du bouton d'enregistrement.
 *
 * CE COMPOSANT EST PRÉSENTATIONNEL. Des props entrent, `save` et `close`
 * sortent : ni appel réseau, ni toast ici — la page en est propriétaire
 * (docs/LISTES-V2.md, C56).
 */
import { computed, ref, watch } from 'vue'
import Dialog from './Dialog.vue'
import DialogNotice from './DialogNotice.vue'
import Button from '../Button.vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: null },
  editTitle: { type: String, default: null },
  createTitle: { type: String, default: null },
  isEditing: { type: Boolean, default: false },
  subtitle: { type: String, default: null },
  size: {
    type: String,
    default: 'medium',
    validator: (v) => ['small', 'medium', 'large', 'xlarge'].includes(v)
  },
  loading: { type: Boolean, default: false },
  saveDisabled: { type: Boolean, default: false },
  /** Consultation seule : le bouton d'enregistrement DISPARAÎT au lieu d'être
   *  grisé — un bouton grisé sans explication se lit comme une panne. */
  hideSave: { type: Boolean, default: false },
  saveLabel: { type: String, default: 'Enregistrer' },
  savingLabel: { type: String, default: 'Enregistrement…' },
  cancelLabel: { type: String, default: 'Annuler' },
  saveIcon: { type: String, default: null },
  saveVariant: { type: String, default: 'primary' },
  /** Message d'échec du serveur. Non nul, il coiffe le corps de la fenêtre. */
  error: { type: String, default: null },
  errorTitle: { type: String, default: "L'enregistrement a échoué" },
  /** Vrai dès qu'un champ a été touché sans être enregistré. */
  dirty: { type: Boolean, default: false },
  /**
   * Corps ajusté (cf. `Dialog`) : la fenêtre prend une hauteur définie, le
   * formulaire distribue au lieu de défiler, et le champ qui porte la classe
   * élastique — une zone de saisie longue — prend ce qui reste. La dernière
   * case et le pied cessent alors de passer sous la ligne de flottaison.
   */
  bodyFit: { type: Boolean, default: false },
  /**
   * Corps sans rembourrage (cf. `Dialog`) : le formulaire fournit sa propre
   * mise en page — un rail de sections et sa zone de contenu, chacun avec son
   * fond et son défilement. Le bandeau d'échec (`error`) reste au-dessus, hors
   * de cette mise en page, pour ne pas défiler avec elle.
   *
   * Opt-in, comme `bodyFit` : sans la prop, le corps garde son rembourrage et
   * pas une fenêtre existante ne bouge.
   */
  flush: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'close', 'save'])

const abandonAsked = ref(false)

// Pendant un enregistrement, la garde n'a plus lieu d'être : la saisie part.
const isGuarded = computed(() => props.dirty && !props.loading)

const computedTitle = computed(() => {
  if (props.title) return props.title
  if (props.isEditing && props.editTitle) return props.editTitle
  if (!props.isEditing && props.createTitle) return props.createTitle
  return ''
})

const askAbandon = () => {
  abandonAsked.value = true
}

const askClose = () => {
  if (isGuarded.value) askAbandon()
  else emit('close')
}

const confirmAbandon = () => {
  abandonAsked.value = false
  emit('close')
}

// Une fenêtre rouverte ne doit pas retrouver la garde de la fois précédente.
watch(() => props.modelValue, (open) => {
  if (!open) abandonAsked.value = false
})
</script>

<style scoped>
.dlgf-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-5);
}

/* Corps sans rembourrage : le bandeau d'échec reprend celui du corps, sinon il
   se colle aux quatre bords de la fenêtre. */
.dlgf-error-flush {
  flex: 0 0 auto;
  padding: var(--spacing-md) var(--spacing-6) 0;
}

/* Le formulaire prend toute la hauteur du corps pour pouvoir la redistribuer.
   PAS de `min-height: 0` : il doit garder son `min-height: auto`, c'est-à-dire
   la somme des minima de ses champs. C'est ce qui l'empêche d'écraser une zone
   de saisie sous sa barre d'outils quand la place manque — il déborde alors, et
   le corps défile. */
.dlgf-form-fit {
  flex: 1 1 auto;
}

/* Même raison que dans `DialogSection` : les champs du kit portent une marge
   basse pensée pour un formulaire de PAGE, qui s'ajoute ici à l'écart de la
   pile — 36 px sous chaque champ au lieu de 20. Un corps qui distribue sa
   hauteur ne peut pas se permettre ces 16 px en triple : ce sont autant de
   pixels pris à la zone d'écriture. Confiné au mode ajusté, il ne déplace donc
   rien dans les fenêtres qui ne le demandent pas. */
.dlgf-form-fit :deep(.form-group),
.dlgf-form-fit :deep(.checkbox-wrapper) {
  margin-bottom: 0;
}

.dlgf-submit {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.dlgf-guard {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-6);
}

.dlgf-guard-box {
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: var(--spacing-5);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.dlgf-guard-title {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.dlgf-guard-text {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
}

.dlgf-guard-actions {
  display: flex;
  gap: var(--spacing-2);
  justify-content: flex-end;
  flex-wrap: wrap;
}

.dlgf-guard-enter-active,
.dlgf-guard-leave-active { transition: opacity 0.18s ease; }
.dlgf-guard-enter-from,
.dlgf-guard-leave-to { opacity: 0; }

@media (max-width: 640px) {
  .dlgf-guard-actions { flex-direction: column-reverse; }
  .dlgf-guard-actions :deep(.btn) { width: 100%; min-height: 48px; }
}
</style>
